import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mammoth from 'mammoth';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import PDFViewer from '../Files/documents/PDFViewer';

interface CustomFileReaderProps {
  fileUrl?: string;
  content?: string;
  isPdf?: boolean;
  filename: string;
}

/**
 * Helper: fetch a URL as a specific responseType.
 * Uses native fetch() for blob: URLs (axios prepends baseURL to them,
 * breaking the request) and falls back to the configured apis instance
 * for normal API paths.
 */
async function fetchUrl(url: string, responseType: 'blob' | 'arraybuffer' | 'text') {
  const isBlobUrl = url.startsWith('blob:');

  if (isBlobUrl) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch blob URL: ${res.statusText}`);
    const contentType = res.headers.get('content-type') || 'application/octet-stream';

    if (responseType === 'arraybuffer') {
      return { data: await res.arrayBuffer(), contentType };
    } else if (responseType === 'blob') {
      return { data: await res.blob(), contentType };
    } else {
      return { data: await res.text(), contentType };
    }
  }

  // Regular API path — use configured axios instance
  const response = await apis.get(url, { responseType });
  return {
    data: response.data,
    contentType: (response.headers?.['content-type'] as string) || 'application/octet-stream',
  };
}

const CustomFileReader: React.FC<CustomFileReaderProps> = ({ fileUrl, content, isPdf, filename }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fetchedContent, setFetchedContent] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isHtmlContent, setIsHtmlContent] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  // Determine file type from filename
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const isFilePdf = isPdf || ext === 'pdf';
  const isDocx = ext === 'docx';
  const isDoc = ext === 'doc';
  const isOfficeDoc = ['xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  const isPlainText = ['txt', 'md', 'csv', 'json', 'xml', 'log'].includes(ext);
  // Browser-renderable image formats — previewed inline via <img>. Non-web
  // formats (tiff/tif) fall through to the download fallback below.
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext);

  useEffect(() => {
    // If content is provided directly, no need to fetch
    if (content) {
      setIsLoading(false);
      return;
    }

    if (!fileUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setBlobUrl(null);
    setFetchedContent(null);
    setFetchError(null);
    setIsHtmlContent(false);

    const fetchFile = async () => {
      try {
        if (isFilePdf) {
          // PDFs: fetch as blob, create object URL for PDFViewer
          const { data } = await fetchUrl(fileUrl, 'blob');
          const blob = new Blob([data as Blob], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else if (isDocx) {
          // DOCX: fetch as arraybuffer, convert to HTML via mammoth
          const { data } = await fetchUrl(fileUrl, 'arraybuffer');
          const arrayBuffer = data as ArrayBuffer;
          try {
            const result = await mammoth.convertToHtml({ arrayBuffer });
            if (result.value) {
              setFetchedContent(result.value);
              setIsHtmlContent(true);
            } else {
              setFetchError(t('Document appears to be empty.'));
            }
          } catch (mammothErr) {
            console.error('Mammoth conversion error:', mammothErr);
            setFetchError(t('Failed to convert document. The file may be corrupted.'));
          }
        } else if (isDoc) {
          // Legacy .doc: show download prompt (mammoth doesn't support .doc)
          const { data, contentType: ct } = await fetchUrl(fileUrl, 'blob');
          const blob = new Blob([data as Blob], {
            type: ct || 'application/msword',
          });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else if (isPlainText) {
          // Plain text files: fetch as text
          const { data } = await fetchUrl(fileUrl, 'text');
          setFetchedContent(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
        } else if (isOfficeDoc) {
          // Other Office docs: fetch as blob and create object URL
          const { data, contentType: ct } = await fetchUrl(fileUrl, 'blob');
          const blob = new Blob([data as Blob], {
            type: ct || 'application/octet-stream',
          });
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setBlobUrl(url);
        } else {
          // Other types: try to fetch as blob and create object URL
          const { data, contentType } = await fetchUrl(fileUrl, 'blob');
          const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });

          // If content type is text-like, read it as text
          if (contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('xml')) {
            const text = await blob.text();
            setFetchedContent(text);
          } else {
            const url = URL.createObjectURL(blob);
            blobUrlRef.current = url;
            setBlobUrl(url);
          }
        }
      } catch (error) {
        console.error('Error fetching file:', error);
        setFetchError(t('Failed to load file. Please try again.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [fileUrl, content, isFilePdf, isDocx, isDoc, isOfficeDoc, isPlainText]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-gray-800">
        {/* Toolbar skeleton */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
          <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
          <div className="flex-1" />
          <div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
        </div>

        {/* Document body skeleton */}
        <div className="flex-1 overflow-hidden px-10 py-8 space-y-6 animate-fadePulse">
          {/* Title area */}
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/5" />
            <div className="h-3 bg-gray-100 dark:bg-gray-750 rounded w-1/4" />
          </div>

          {/* Paragraph 1 */}
          <div className="space-y-2.5 pt-2">
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-11/12" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
          </div>

          {/* Paragraph 2 */}
          <div className="space-y-2.5 pt-2">
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          </div>

          {/* Subheading */}
          <div className="pt-3">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/5" />
          </div>

          {/* Paragraph 3 */}
          <div className="space-y-2.5">
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
            <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          </div>

          {/* Paragraph 4 */}
          <div className="space-y-2.5 pt-2">
            <div className="h-3.5 bg-gray-100 dark:bg-gray-750 rounded w-full" />
            <div className="h-3.5 bg-gray-100 dark:bg-gray-750 rounded w-full" />
            <div className="h-3.5 bg-gray-100 dark:bg-gray-750 rounded w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-danger-500 text-sm">{fetchError}</p>
      </div>
    );
  }

  // Resolve content: prefer prop, then fetched
  const displayContent = content || fetchedContent;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      <div className="flex-1 overflow-y-auto">
        {isFilePdf ? (
          blobUrl ? (
            <PDFViewer pdfUrl={blobUrl} />
          ) : (
            <p className="text-danger-500 p-6">{t('Failed to load PDF. Please try again or check the URL.')}</p>
          )
        ) : isHtmlContent && displayContent ? (
          <div className="w-full">
            <div 
              className="prose dark:prose-invert max-w-none px-10 py-8"
              style={{
                fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                lineHeight: 1.7,
                color: '#1a1a1a',
                fontSize: '15px',
              }}
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />
          </div>
        ) : displayContent && (displayContent.includes('<!DOCTYPE') || displayContent.includes('<html') || displayContent.includes('<body')) ? (
          <iframe
            title={filename || 'Document'}
            srcDoc={displayContent}
            className="w-full h-full border-0"
            style={{ minHeight: '80vh' }}
            sandbox="allow-same-origin"
          />
        ) : displayContent ? (
          <div className="prose dark:prose-invert max-w-none px-10 py-8">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {displayContent}
            </ReactMarkdown>
          </div>
        ) : isImage && blobUrl ? (
          <div className="flex items-center justify-center min-h-full p-6">
            <img
              src={blobUrl}
              alt={filename}
              className="max-w-full max-h-[80vh] h-auto object-contain rounded"
            />
          </div>
        ) : blobUrl ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('Preview is not available for this file type.')}
            </p>
            <a
              href={blobUrl}
              download={filename}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-info-600 dark:text-info-400 bg-info-50 dark:bg-info-900/20 rounded-lg hover:bg-info-100 dark:hover:bg-info-900/30 transition-colors"
            >
              {t('Download')} {filename}
            </a>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full p-8">
            <p className="text-sm text-gray-400 dark:text-gray-500">{t('No content available')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFileReader;